import * as Immutable from 'immutable';
import { Steps } from 'intro.js-react';
import 'intro.js/introjs.css';
import * as React from 'react';
import * as ReactDOMServer from 'react-dom/server';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';

import { Walkthrough } from '../../components/Walkthrough';
import { StateType } from '../../reducers';
import { getUserIdSelector, getUserPermissionsSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { intlFormatMessage } from '../../shared/helpers';
import { formatHTMLMessageHelper } from '../../shared/helpers/internationalization';
import { setLocalStorageItem } from '../../shared/helpers/local-storage';
import analytics from '../../shared/services/analytics/analytics';

interface DashboardIntroProps {
  handleOnExitIntro: () => void;
}

interface DashboardIntroStateProps {
  userId: string;
  userPermissions: Immutable.List<string>;
}

type DashboardIntroPropsGeneric = InjectedIntlProps & DashboardIntroProps & DashboardIntroStateProps;

export class DashboardIntro extends React.Component<DashboardIntroPropsGeneric, {}> {
  walkthroughSteps: any;

  handleOnExit = () => {
    this.props.handleOnExitIntro();
    this.handleOnComplete();
  };

  handleRefStep = (ref: any) => {
    this.walkthroughSteps = ref;
  };

  openDropdown(selector: any) {
    const dropdown: any = document.querySelector(`.${selector}`);
    const menu: any = document.querySelector(`.${selector} .menu`);

    if (dropdown && menu) {
      dropdown.className = dropdown.className.concat(' active visible');
      menu.className = menu.className.concat(' visible');
      dropdown.setAttribute('aria-expanded', true);
    }
  }

  closeDropdown(selector: string) {
    const dropdown: any = document.querySelector(`.${selector}`);
    const menu: any = document.querySelector(`.${selector} .menu`);

    if (dropdown && menu) {
      dropdown.className = dropdown.className.replace('active visible', '');
      menu.className = menu.className.replace('visible', '');
      dropdown.setAttribute('aria-expanded', false);
    }
  }

  onBeforeChange = (nextStepIndex: any) => {
    const isConsultant = this.isConsultant();

    // Access internal Intro.js state to determine if the user is moving forward or backward
    const isMovingForward = this.walkthroughSteps?.introJs?._direction === 'forward';
    // Track continue clicks only in forward direction (not for the last step — that's handled by handleOnComplete)
    if (isMovingForward) {
      const eventName = isConsultant ? 'dashboard_minitour_consultant_click_continue' : 'dashboard_minitour_user_click_continue';
      analytics.trackCustomEvent(eventName, { event_label: String(nextStepIndex) });
    }

    switch (nextStepIndex) {
      case 0:
        this.closeDropdown('header__nav-auth-user');
        break;
      case 1:
        this.openDropdown('header__nav-auth-user');
        this.walkthroughSteps.updateStepElement(nextStepIndex);
        break;
      case 2:
        this.closeDropdown('header__nav-auth-user');
        break;
      case 5:
        this.walkthroughSteps.updateStepElement(nextStepIndex);
        break;
      default:
        return;
    }
  };

  isConsultant() {
    return this.props.userPermissions.includes('CONSULTANT') || this.props.userPermissions.includes('RESEARCHER');
  }

  getStepsConsultant() {
    const { intl } = this.props;

    return [
      {
        element: '.intro-dashboard-step',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={1}
            total_steps={5}
            title={intlFormatMessage('intro.Dashboard-consultant.1.title', intl)}
            content={
              <p>
                <p>{intlFormatMessage('intro.Dashboard-consultant.1.content', intl)}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-dashboard-step-2',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={2}
            total_steps={5}
            title={intlFormatMessage('intro.Dashboard-consultant.2.title', intl)}
            content={
              <p>
                <p>{formatHTMLMessageHelper(intl, 'intro.Dashboard-consultant.2.content')}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-dashboard-step-3',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={3}
            total_steps={5}
            title={intlFormatMessage('intro.Dashboard-consultant.3.title', intl)}
            content={
              <p>
                <p>{formatHTMLMessageHelper(intl, 'intro.Dashboard-consultant.3.content')}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-dashboard-step-consultant-4',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={4}
            total_steps={5}
            title={intlFormatMessage('intro.Dashboard-consultant.4.title', intl)}
            content={
              <p>
                <p>{formatHTMLMessageHelper(intl, 'intro.Dashboard-consultant.4.content')}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-dashboard-step-consultant-5',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={5}
            total_steps={5}
            title={formatHTMLMessageHelper(intl, 'intro.Dashboard-consultant.5.title')}
            content={
              <p>
                <p>{formatHTMLMessageHelper(intl, 'intro.Dashboard-consultant.5.content')}</p>
              </p>
            }
          />
        ),
      },
    ];
  }

  getSteps() {
    const { intl } = this.props;

    return [
      {
        element: '.intro-dashboard-step-1',
        tooltipClass: 'tooltip-dashboard-step-1-user',
        intro: ReactDOMServer.renderToString(
          <Walkthrough current_step={1} total_steps={8} title={intlFormatMessage('intro.Dashboard.1.title', intl)} content={''} />
        ),
      },
      {
        element: '.intro-dashboard-step-2',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={2}
            total_steps={8}
            title={intlFormatMessage('intro.Dashboard.2.title', intl)}
            content={
              <p>
                {formatHTMLMessageHelper(intl, 'intro.Dashboard.2.content.1')}
                <p>{formatHTMLMessageHelper(intl, 'intro.Dashboard.2.content.2')}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-dashboard-step-3',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={3}
            total_steps={8}
            title={intlFormatMessage('intro.Dashboard.3.title', intl)}
            content={
              <p>
                <p>{formatHTMLMessageHelper(intl, 'intro.Dashboard.3.content')}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-dashboard-step-4',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={4}
            total_steps={8}
            title={intlFormatMessage('intro.Dashboard.4.title', intl)}
            content={
              <p>
                <p>{formatHTMLMessageHelper(intl, 'intro.Dashboard.4.content')}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-dashboard-step-5',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={5}
            total_steps={8}
            title={intlFormatMessage('intro.Dashboard.5.title', intl)}
            content={
              <p>
                <p>{intlFormatMessage('intro.Dashboard.5.content', intl)}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-dashboard-step-6 .text',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={6}
            total_steps={8}
            title={intlFormatMessage('intro.Dashboard.6.title', intl)}
            content={
              <p>
                <p>{formatHTMLMessageHelper(intl, 'intro.Dashboard.6.content.1')}</p>
                <p>{formatHTMLMessageHelper(intl, 'intro.Dashboard.6.content.2')}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.intro-dashboard-step-7 .text',
        intro: ReactDOMServer.renderToString(
          <Walkthrough
            current_step={7}
            total_steps={8}
            title={intlFormatMessage('intro.Dashboard.7.title', intl)}
            content={
              <p>
                <p>{intlFormatMessage('intro.Dashboard.7.content', intl)}</p>
              </p>
            }
          />
        ),
      },
      {
        element: '.card__footer-title--intro-dashboard-step-8',
        intro: ReactDOMServer.renderToString(
          <Walkthrough current_step={8} total_steps={8} title={intlFormatMessage('intro.Dashboard.8.title', intl)} content={''} />
        ),
      },
    ];
  }

  handleOnComplete = () => {
    const isConsultant = this.isConsultant();
    analytics.trackCustomEvent(isConsultant ? 'dashboard_minitour_consultant_click_done' : 'dashboard_minitour_user_click_done');

    const walkthrough = isConsultant ? 'dashboardWalkthrough-consultant' : 'dashboardWalkthrough';
    setLocalStorageItem(this.props.userId, walkthrough, 'true');
    this.closeDropdown('header__nav-auth-user');
  };

  render() {
    const steps =
      this.props.userPermissions.includes('CONSULTANT') || this.props.userPermissions.includes('RESEARCHER')
        ? this.getStepsConsultant()
        : this.getSteps();

    return (
      <Steps
        ref={this.handleRefStep}
        steps={steps}
        enabled={true}
        initialStep={0}
        onExit={this.handleOnExit}
        onBeforeChange={this.onBeforeChange}
        options={{
          tooltipClass: 'introjs-websim',
          hidePrev: true,
          hideNext: true,
          showBullets: false,
          showStepNumbers: false,
          keyboardNavigation: false,
          exitOnOverlayClick: false,
          nextLabel: 'Continue',
          prevLabel: 'Previous',
          disableInteraction: true,
        }}
      />
    );
  }
}

const mapStateToProps = (state: StateType): DashboardIntroStateProps => ({
  // @ts-ignore
  userId: getUserIdSelector(state),
  // @ts-ignore
  userPermissions: getUserPermissionsSelector(state),
});

export default injectIntl(connect<DashboardIntroStateProps, {}, any, StateType>(mapStateToProps, {})(DashboardIntro));
